import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Link as RouterLink } from "react-router-dom"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import { isAccountOwner, isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import ConciergeInboxTemplateViewDialog, {
  useConciergeInboxTemplateViewDialogState,
} from "../components/ConciergeInboxTemplateViewDialog"
import InboxTemplateStatusChip from "../components/InboxTemplateStatusChip"

const APPROVALS_URL = "/crm/api/concierge/inbox-template-approvals/"
const VISIBLE_TEMPLATE_ROWS = 4
const TEMPLATE_ROW_HEIGHT_PX = 88
const TEMPLATE_LIST_GAP_PX = 16
const TEMPLATE_LIST_MAX_HEIGHT_PX =
  VISIBLE_TEMPLATE_ROWS * TEMPLATE_ROW_HEIGHT_PX +
  (VISIBLE_TEMPLATE_ROWS - 1) * TEMPLATE_LIST_GAP_PX
const EMAIL_INBOX_TEMPLATES_URL = "/crm/api/zoho-mail/inbox/templates/"
const SMS_INBOX_TEMPLATES_URL = "/crm/api/concierge-sms/inbox/templates/"

function normalizeTemplateRow(row, medium) {
  if (row.medium) {
    return row
  }
  return {
    id: row.id,
    medium,
    name: row.name,
    preview:
      medium === "email"
        ? row.subject || row.name
        : (row.text || row.name || "").slice(0, 160),
    approval_status: row.approval_status,
  }
}

function TemplateRow({
  template,
  canApprove,
  actingId,
  onReject,
  onInactive,
  onReactivate,
  onView,
}) {
  const busy = actingId === `${template.medium}-${template.id}`
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minHeight: TEMPLATE_ROW_HEIGHT_PX,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Row
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Column sx={{ minWidth: 0, gap: 0.75, flex: 1 }}>
          <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography fontWeight={700}>{template.name}</Typography>
            <Chip size="small" label="Concierge inbox" color="secondary" />
            <InboxTemplateStatusChip status={template.approval_status} />
          </Row>
          <Typography variant="body2" color="text.secondary" noWrap>
            {template.preview || "No preview"}
          </Typography>
        </Column>
        <Row sx={{ gap: 1, flexWrap: "wrap" }}>
          {canApprove ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={() => onView(template)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              View
            </Button>
          ) : null}
          {canApprove && template.approval_status === "approved" ? (
            <>
              <LoadingButton
                size="small"
                variant="outlined"
                color="warning"
                loading={busy}
                onClick={() => onInactive(template)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Inactive
              </LoadingButton>
              <LoadingButton
                size="small"
                variant="outlined"
                color="error"
                loading={busy}
                startIcon={<CloseOutlinedIcon />}
                onClick={() => onReject(template)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Reject
              </LoadingButton>
            </>
          ) : null}
          {canApprove && template.approval_status === "inactive" ? (
            <LoadingButton
              size="small"
              variant="contained"
              color="success"
              loading={busy}
              startIcon={<CheckOutlinedIcon />}
              onClick={() => onReactivate(template)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Reactivate
            </LoadingButton>
          ) : null}
        </Row>
      </Row>
    </Paper>
  )
}

export default function ConciergeInboxTemplatesPanel({
  medium,
  onStatusMessage,
}) {
  const axios = useAuthorizedAxiosClient()
  const profile = useSelector(selectProfile)
  const owner = isAccountOwner(profile)
  const concierge = isConcierge(profile)
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [actingId, setActingId] = useState(null)
  const [statusFilter, setStatusFilter] = useState("all")
  const { viewTemplate, viewDialogOpen, openViewDialog, closeViewDialog } =
    useConciergeInboxTemplateViewDialogState()

  const loadTemplates = useCallback(() => {
    if (!owner && !concierge) {
      setTemplates([])
      return Promise.resolve()
    }
    setIsLoading(true)
    const inboxUrl =
      medium === "email" ? EMAIL_INBOX_TEMPLATES_URL : SMS_INBOX_TEMPLATES_URL
    const request = owner
      ? axios.get(APPROVALS_URL, {
          params: {
            approval_status: statusFilter === "all" ? "all" : statusFilter,
          },
        })
      : Promise.all([
          axios.get(inboxUrl),
          axios.get(inboxUrl, { params: { scope: "account" } }),
        ]).then(([picker, account]) => {
          const byId = new Map()
          ;[
            ...(Array.isArray(picker.data)
              ? picker.data
              : picker.data?.results || []),
            ...(Array.isArray(account.data)
              ? account.data
              : account.data?.results || []),
          ].forEach((row) => {
            if (row?.id != null) byId.set(row.id, row)
          })
          return { data: [...byId.values()] }
        })

    return request
      .then(({ data }) => {
        const rows = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
          ? data
          : []
        const normalized = rows
          .map((row) => normalizeTemplateRow(row, medium))
          .filter((row) => row.medium === medium)
        if (!owner && statusFilter !== "all") {
          setTemplates(
            normalized.filter((row) => row.approval_status === statusFilter)
          )
          return
        }
        setTemplates(normalized)
      })
      .catch(({ response }) => {
        setTemplates([])
        onStatusMessage?.(
          parseApiErrorMessage(
            response?.data,
            "Could not load concierge templates"
          ),
          "error"
        )
      })
      .finally(() => setIsLoading(false))
  }, [axios, concierge, medium, onStatusMessage, owner, statusFilter])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  async function handleAction(template, action) {
    const key = `${template.medium}-${template.id}`
    setActingId(key)
    try {
      await axios.post(
        `${APPROVALS_URL}${template.medium}/${template.id}/${action}/`
      )
      const messages = {
        reject: `${template.name} rejected.`,
        inactive: `${template.name} deactivated.`,
        reactivate: `${template.name} reactivated.`,
      }
      onStatusMessage?.(
        messages[action] || `${template.name} updated.`,
        action === "reactivate" ? "success" : "info"
      )
      loadTemplates()
    } catch ({ response }) {
      onStatusMessage?.(
        parseApiErrorMessage(
          response?.data,
          "Could not update template approval"
        ),
        "error"
      )
    } finally {
      setActingId(null)
    }
  }

  if (!owner && !concierge) {
    return null
  }

  const inactiveCount = templates.filter(
    (template) => template.approval_status === "inactive"
  ).length
  const mediumLabel = medium === "sms" ? "SMS" : "Email"

  return (
    <Box
      sx={{
        mt: 3,
        mb: 3,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
      }}
    >
      <Column sx={{ gap: 2 }}>
        <Column sx={{ gap: 0.75 }}>
          <Typography variant="h6" fontWeight={700}>
            Concierge {mediumLabel} inbox templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {owner
              ? `Manage ${mediumLabel.toLowerCase()} reply templates your concierge creates in Messages. Concierges run AI review, verify the content, then save; you can reject or deactivate templates afterward.`
              : `Approved and default ${mediumLabel.toLowerCase()} templates appear throughout the CRM. Create and edit account templates in Messages with AI review before save.`}
          </Typography>
          {concierge ? (
            <Typography variant="body2">
              Create and edit templates in{" "}
              <Link component={RouterLink} to="/messages">
                Messages
              </Link>
              .
            </Typography>
          ) : null}
        </Column>

        <Row sx={{ gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { value: "all", label: "All" },
            { value: "approved", label: "Approved" },
            { value: "inactive", label: "Inactive" },
            { value: "rejected", label: "Rejected" },
          ].map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              color={statusFilter === option.value ? "secondary" : "default"}
              onClick={() => setStatusFilter(option.value)}
              sx={{ fontWeight: 600 }}
            />
          ))}
          {inactiveCount > 0 ? (
            <Chip
              size="small"
              color="default"
              label={`${inactiveCount} inactive`}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Row>

        <ConditionalPurpleLinearProgress visible={isLoading} />

        {!isLoading && templates.length === 0 ? (
          <Alert severity="info">
            {`No concierge ${mediumLabel.toLowerCase()} inbox templates in this view.`}
          </Alert>
        ) : null}

        {!isLoading && templates.length > 0 ? (
          <Column
            sx={{
              gap: 2,
              maxHeight: TEMPLATE_LIST_MAX_HEIGHT_PX,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {templates.map((template) => (
              <TemplateRow
                key={`${template.medium}-${template.id}`}
                template={template}
                canApprove={owner}
                actingId={actingId}
                onReject={(item) => handleAction(item, "reject")}
                onInactive={(item) => handleAction(item, "inactive")}
                onReactivate={(item) => handleAction(item, "reactivate")}
                onView={openViewDialog}
              />
            ))}
          </Column>
        ) : null}
      </Column>
      <ConciergeInboxTemplateViewDialog
        open={viewDialogOpen}
        template={viewTemplate}
        onClose={closeViewDialog}
      />
    </Box>
  )
}
